import { useState } from 'react';
import { toast } from 'react-toastify';
import { IAssistantMessage } from 'shared';
import {
  CheckCircleIcon,
  EnvelopeIcon,
  PaperClipIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { Button } from '../../../common/Atoms/Button';
import { Modal } from '../../../common/Atoms/Modal';
import { Select } from '../../../common/Atoms/Select';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';
import { classNames } from '../../../common/lib/classNames';
import { useGetSelectedOrganisation } from '../../hooks/useGetSelectedOrganisation';
import { useSendAssistantEmailDraftMutation } from '../../services/api/assistantApi/assistant';
import { useFetchSettingsQuery } from '../../services/api/settingsApi/settings';
import { CustomIframe } from '../IFrame';

// Same limits as the contact page composer
const BODY_MIN_LENGTH = 4;
const BODY_MAX_LENGTH = 10000;
const SUBJECT_MAX_LENGTH = 255;

const RECIPIENT_CLASS = `inline-flex cursor-pointer items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition`;
const INPUT_CLASS = `w-full rounded-md border border-gray-300 p-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-indigo-500`;

interface EmailDraftCardProps {
  message: IAssistantMessage;
  onDismiss: () => Promise<void>;
  busy: boolean;
  /** When set, send/dismiss are hidden and this explains why */
  readOnly?: string;
}

/** An assistant-drafted email to a customer, opened in a large editor to tweak and send */
export function EmailDraftCard({ message, onDismiss, busy, readOnly }: EmailDraftCardProps) {
  const draft = message.emailDraft;
  const organisation = useGetSelectedOrganisation();
  const organisationId = organisation?.id;
  const { data: settings } = useFetchSettingsQuery(organisationId as string, { skip: !organisationId });
  const [send, { isLoading: sending }] = useSendAssistantEmailDraftMutation();
  const [pickedProfileId, setPickedProfileId] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [toEmails, setToEmails] = useState(draft?.toEmails ?? []);
  const [subject, setSubject] = useState(draft?.subject ?? ``);
  const [body, setBody] = useState(draft?.body ?? ``);
  const [attach, setAttach] = useState(!!draft?.invoiceId);
  const [error, setError] = useState<string | null>(null);

  if (!draft) {
    return null;
  }

  const status = message.proposalStatus;
  const editable = status === `pending` && !readOnly;
  const profiles = settings?.senderProfiles ?? [];
  const profile = profiles.find(item => item.id === pickedProfileId) ?? profiles.find(item => item.default);
  // The server appends the profile's signature; without one, sign off with the organisation's name instead
  const signOff = profile?.signatureHtml ? `` : `\n\nKind regards,\n${organisation?.customerFacingName ?? ``}`.trimEnd();
  // The sign-off is sent as part of the body, so it counts towards the server's limit
  const bodyMaxLength = BODY_MAX_LENGTH - signOff.length;
  const canSend = !!toEmails.length && !!subject.trim() && body.trim().length >= BODY_MIN_LENGTH && body.trim().length <= bodyMaxLength;

  function toggleRecipient(email: string) {
    setToEmails(current => current.includes(email) ? current.filter(item => item !== email) : [...current, email]);
  }

  async function onSend() {
    if (!organisationId || !canSend) {
      return;
    }

    setError(null);

    try {
      await send({
        organisationId,
        messageId: message.id,
        toEmails,
        subject: subject.trim(),
        body: body.trim() + signOff,
        invoiceId: attach ? draft!.invoiceId : null,
        senderProfileId: profile?.id ?? null,
      }).unwrap();

      setOpen(false);
      toast.success(`Email sent to ${draft!.contactName}`);
    }
    catch (err) {
      setError((err as { data?: { message?: string } })?.data?.message || `Could not send the email. Please try again.`);
    }
  }

  return (
    <div className={ `w-full rounded-xl border border-slate-200 bg-white p-4 shadow-xs` }>
      <div className={ `flex items-start gap-2` }>
        <EnvelopeIcon className={ `mt-0.5 h-4 w-4 shrink-0 text-indigo-500` } />
        <div className={ `min-w-0 flex-1` }>
          <Paragraph className={ `truncate` }>{ `Email to ${draft.contactName}` }</Paragraph>
          <p className={ `truncate text-xs text-slate-500` }>{ subject }</p>
        </div>
      </div>
      <p className={ `mt-2 line-clamp-3 whitespace-pre-wrap text-xs text-slate-600` }>{ body }</p>
      <div className={ `mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-3` }>
        {
          status === `pending` && readOnly &&
          <Paragraph
            size={ `xs` }
            variant={ `secondary` }
          >
            { readOnly }
          </Paragraph>
        }
        {
          editable &&
          <>
            <Paragraph
              size={ `xs` }
              variant={ `secondary` }
            >
              { `Nothing is sent until you send it.` }
            </Paragraph>
            <div className={ `flex gap-2` }>
              <Button
                size={ `sm` }
                color={ `gray` }
                disabled={ busy || sending }
                onClick={ onDismiss }
              >
                { `Dismiss` }
              </Button>
              <Button
                size={ `sm` }
                disabled={ busy }
                onClick={ () => setOpen(true) }
              >
                { `Open draft` }
              </Button>
            </div>
          </>
        }
        {
          status === `applied` &&
          <Paragraph
            size={ `xs` }
            className={ `flex items-center gap-1 !text-green-700` }
          >
            <CheckCircleIcon className={ `h-4 w-4` } />
            { `Sent` }
          </Paragraph>
        }
        {
          status === `dismissed` &&
          <Paragraph
            size={ `xs` }
            variant={ `secondary` }
          >
            { `Dismissed` }
          </Paragraph>
        }
      </div>

      <Modal
        open={ open }
        setOpen={ setOpen }
        width={ `lg` }
        title={ `Email to ${draft.contactName}` }
      >
        <div className={ `space-y-3` }>
          {
            profiles.length > 1 &&
            <Select
              label={ `Send from` }
              name={ `senderProfileId` }
              options={ profiles.map(item => ({ label: item.name, value: item.id as number, badge: item.default ? `Default` : undefined, badgeColor: item.default ? `blue` : undefined })) }
              selected={ profile?.id }
              onChange={ ({ value }) => setPickedProfileId(Number(value)) }
            />
          }
          <div>
            <span className={ `mb-1 block text-xs font-medium text-gray-600` }>{ `To` }</span>
            <div className={ `flex flex-wrap gap-1.5` }>
              {
                draft.availableEmails.map(email => {
                  const selected = toEmails.includes(email);

                  return (
                    <button
                      key={ email }
                      type={ `button` }
                      aria-pressed={ selected }
                      onClick={ () => toggleRecipient(email) }
                      className={ classNames(RECIPIENT_CLASS, selected ? `border-indigo-300 bg-indigo-50 text-indigo-800` : `border-dashed border-gray-300 bg-white text-gray-500 hover:bg-gray-50`) }
                    >
                      { selected ? <XMarkIcon className={ `h-3.5 w-3.5` } /> : <PlusIcon className={ `h-3.5 w-3.5` } /> }
                      { email }
                    </button>
                  );
                })
              }
            </div>
          </div>
          <input
            type={ `text` }
            aria-label={ `Subject` }
            className={ INPUT_CLASS }
            value={ subject }
            maxLength={ SUBJECT_MAX_LENGTH }
            onChange={ event => setSubject(event.target.value) }
            placeholder={ `Subject` }
          />
          <textarea
            aria-label={ `Email body` }
            className={ classNames(INPUT_CLASS, `min-h-[18rem] resize-y`) }
            value={ body }
            maxLength={ bodyMaxLength }
            onChange={ event => setBody(event.target.value) }
          />
          <div className={ `rounded-md border border-dashed border-gray-200 bg-gray-50 p-2` }>
            <span className={ `mb-1 block text-[11px] font-medium text-gray-500` }>{ profile?.signatureHtml ? `Signature from ${profile.name}` : `Added below your message` }</span>
            {
              profile?.signatureHtml
                // Sandboxed full document (no scripts), same as other email previews
                ? <CustomIframe
                  title={ `Email signature` }
                  className={ `h-32 w-full bg-white` }
                  srcDoc={ `<html><body style="margin:8px;font-family:sans-serif;font-size:14px">${profile.signatureHtml}</body></html>` }
                />
                : <p className={ `whitespace-pre-wrap text-sm text-gray-700` }>{ signOff.trim() }</p>
            }
          </div>
          {
            draft.invoiceId &&
            <label className={ `flex cursor-pointer items-center gap-2 text-sm text-gray-700` }>
              <input
                type={ `checkbox` }
                className={ `cursor-pointer` }
                checked={ attach }
                onChange={ event => setAttach(event.target.checked) }
              />
              <PaperClipIcon className={ `h-4 w-4 text-gray-400` } />
              { `Attach invoice ${draft.invoiceNumber ?? ``} PDF` }
            </label>
          }
          <div className={ `flex items-center justify-between gap-2` }>
            <span className={ `text-xs text-red-600` }>{ error }</span>
            <div className={ `flex gap-2` }>
              <Button
                color={ `gray` }
                disabled={ sending }
                onClick={ () => setOpen(false) }
              >
                { `Cancel` }
              </Button>
              <Button
                color={ `paidniceGreen` }
                disabled={ !canSend || sending }
                tooltip={ body.trim().length > bodyMaxLength ? `The email is ${body.trim().length - bodyMaxLength} characters too long` : undefined }
                loading={ sending }
                onClick={ onSend }
              >
                { `Send email` }
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
